import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Injector,
  input,
  inject,
} from '@angular/core';

import { CollapsibleCardAnalyticsDirective } from '@core/candidate-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { Hotspots, IntegrationKinds } from '@core/integrations-types';
import type { DialogWidth } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  CollapsibleCardComponent,
  ConnectedOverlayDirective,
  DialogService,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';

import { ContextualIntegrationsApiService } from '../../../client/services/contextual-integrations-api.service';
import { CurrentHotspotToken } from '../../../client/services/current-hotspot.token';
import { IntegrationKindToken } from '../../../client/services/integration-kind.token';
import { PartnerToken } from '../../../client/services/partner.token';
import { DynamicHelpers } from '../../../dynamic/helpers';
import { SendCandidateDialogComponent } from '../../../dynamic/send-candidate-dialog/send-candidate-dialog.component';
import type { KiwiIntegrationsElement } from '../types';

@Component({
  selector: 'rti-kiwi-hr-integration-card',
  templateUrl: './kiwi-hr-integration-card.component.html',
  styleUrls: ['./kiwi-hr-integration-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CollapsibleCardComponent,
    IconComponent,
    HeadingComponent,
    TranslatePipe,
    IconButtonComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    DropdownComponent,
    DropdownItemComponent,
    ButtonComponent,
    CollapsibleCardAnalyticsDirective,
    PlaceloaderComponent,
  ],
})
export class KiwiHrIntegrationCardComponent {
  private readonly dialogService = inject(DialogService);
  private readonly helpers = inject(DynamicHelpers);
  private readonly injector = inject(Injector);

  public integrationsElement = input.required<KiwiIntegrationsElement>();

  public isLoaded = input(false);

  public isProfileCreated = computed(
    () =>
      this.integrationsElement().integrationKind === IntegrationKinds.KIWI_HR &&
      this.integrationsElement().hotspot.name !== Hotspots.CandidateProfileMore,
  );

  public onMoreClick($event: MouseEvent, moreDropdown: ConnectedOverlayDirective): void {
    $event.stopImmediatePropagation();
    moreDropdown.toggleOverlay(!moreDropdown.open);
  }

  public onSendOrVisitClick(): void {
    if (this.isProfileCreated()) {
      this.onVisitKiwi();
    } else {
      this.onSendToKiwi();
    }
  }

  private onVisitKiwi(): void {
    const visitUrl = this.integrationsElement().inputs.url;

    if (!visitUrl) {
      return;
    }

    window.open(visitUrl, '_blank');
  }

  private onSendToKiwi(): void {
    const dialog = this.integrationsElement().inputs.dialog;
    const candidate = this.integrationsElement().inputs.candidate;

    if (!dialog || !candidate) {
      return;
    }

    this.dialogService.openCustom(
      SendCandidateDialogComponent,
      {
        width: <DialogWidth>`${dialog.widthPx}px`,
        injector: Injector.create({
          parent: this.injector,
          providers: [
            { provide: CurrentHotspotToken, useValue: this.integrationsElement().hotspot },
            { provide: IntegrationKindToken, useValue: this.integrationsElement().integrationKind },
            { provide: PartnerToken, useValue: this.integrationsElement().partner },
            {
              provide: ContextualIntegrationsApiService,
              useClass: ContextualIntegrationsApiService,
            },
          ],
        }),
      },
      {
        title: this.helpers.buildText(dialog.title),
        subtitle: this.helpers.buildText(dialog.subtitle),
        formConfig: dialog.form,
        candidate: candidate,
      },
    );
  }
}
